import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import styles from "./cart.module.css";

import {
  Pagination,
  Loader,
} from "../../../components/shared";

import CartMainContent from "./CartMainContent";

import {
  useDecreaseCartQty,
  useIncreaseCartQty,
  useRemoveCartItem,
} from "../../../queries/useCartQuery";
import Swal from "sweetalert2";
import { useShippingRulesQuery } from "../../../queries/useOrderQuery";
import { useOrderSummary } from "../../../hooks/useOrderSummary";

import {
  useDeleteUserAddressMutation,
  useUserAddressListQuery,
} from "../../../queries/useUserAddress";

import CheckoutShipping from "../Checkout/CheckoutShipping/CheckoutShipping";

import AddMyAddress from "../UserProfileOverview/MyAddress/AddMyAddress/AddMyAddress";
import LocationPicker from "../UserProfileOverview/MyAddress/AddMyAddress/Locationpicker";

import { ErrorFallback, ErrorQueryBoundary } from "../../../components";

import CartOrderSummary from "./CartOrderSummary";

import {
  FaPlus,

  FaRegHeart,
  FaShoppingCart,
} from "react-icons/fa";

import CheckoutStepper from "./Checkoutstepper";


/* =========================================================
   SAFE LOCAL STORAGE PARSER
========================================================= */

const getLocalStorageJSON = (key, fallback = null) => {
  try {
    const saved = localStorage.getItem(key);

    if (!saved) {
      return fallback;
    }

    return JSON.parse(saved);
  } catch (error) {
    console.error(`Failed to parse localStorage: ${key}`, error);

    localStorage.removeItem(key);

    return fallback;
  }
};


/* =========================================================
   GET ID FROM VALUE
========================================================= */

const getIdValue = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "object") {
    return value?.id ?? "";
  }

  return value;
};


/* =========================================================
   GET NAME FROM VALUE
========================================================= */

const getNameValue = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "object") {
    return (
      value?.name ||
      value?.title ||
      value?.label ||
      value?.city ||
      value?.state ||
      value?.country ||
      ""
    );
  }

  return String(value);
};


/* =========================================================
   NORMALIZE ADDRESS
========================================================= */

const normalizeAddress = (address) => {
  if (!address || typeof address !== "object") {
    return null;
  }

  return {
    ...address,

    id: address.id ?? null,

    country_id: getIdValue(address.country_id),

    state_id: getIdValue(address.state_id),

    city_id: getIdValue(address.city_id),

    country_name: getNameValue(address.country_id),

    state_name: getNameValue(address.state_id),

    city_name: getNameValue(address.city),

    city:
      typeof address.city === "object"
        ? getNameValue(address.city)
        : (address.city ?? ""),

    name:
      typeof address.name === "object"
        ? getNameValue(address.name)
        : (address.name ?? ""),

    email: address.email ?? "",

    phone: address.phone ?? "",

    address_type: address.address_type ?? "",

    address: address.address ?? "",

    zip: address.zip ?? "",
  };
};


/* =========================================================
   NORMALIZE ADDRESS RESPONSE
========================================================= */

const getAddressFromResponse = (response) => {
  if (!response) {
    return null;
  }

  if (
    response?.data &&
    typeof response.data === "object" &&
    !Array.isArray(response.data)
  ) {
    return normalizeAddress(response.data);
  }

  return normalizeAddress(response);
};


/* =========================================================
   NORMALIZE ADDRESS LIST
========================================================= */

const normalizeAddressList = (list) => {
  if (!Array.isArray(list)) {
    return [];
  }

  return list
    .map((item) => normalizeAddress(item))
    .filter(Boolean);
};


/* =========================================================
   CART COMPONENT
========================================================= */

const Cart = () => {
  const navigate = useNavigate();


  /* =====================================================
     LOADING STATES
  ===================================================== */

  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const [selectLoading, setSelectLoading] = useState(false);

  const [deleteLoading, setDeleteLoading] = useState(false);


  /* =====================================================
     ADDRESS FORM / LOCATION PICKER
  ===================================================== */

  const [showForm, setShowForm] = useState(false);

  const [showLocationPicker, setShowLocationPicker] =
    useState(false);

  const [locationData, setLocationData] =
    useState(null);
const [editAddress, setEditAddress] = useState(null);

  /* =====================================================
     SELECTED ADDRESS
  ===================================================== */

  const [selectedAddress, setSelectedAddress] =
    useState(() => {
      const saved = getLocalStorageJSON(
        "checkoutAddress",
        null,
      );

      return saved ? normalizeAddress(saved) : null;
    });




  /* =====================================================
     ADDRESS API
  ===================================================== */

  const {
    data: rawAddressList = [],
    isPending: isPendingAddress,
  } = useUserAddressListQuery();


  /* =====================================================
     NORMALIZED ADDRESS LIST
  ===================================================== */

  const addressList =
    normalizeAddressList(rawAddressList);


  /* =====================================================
     SHIPPING
  ===================================================== */

  const {
    data: shippingRulesData = [],
    isPending: isShippingPending,
  } = useShippingRulesQuery();


  const [selectedShipping, setSelectedShipping] =
    useState(() => {
      return getLocalStorageJSON(
        "shipping-rules",
        null,
      );
    });


  /* =====================================================
     CART SUMMARY
  ===================================================== */

  const {
    cartItems = [],
    isPending,
    error,
  } = useOrderSummary(selectedShipping);


  /* =====================================================
     MUTATIONS
  ===================================================== */

  const increaseQty = useIncreaseCartQty();

  const decreaseQty = useDecreaseCartQty();

  const deleteMutation = useRemoveCartItem();
const {
  mutate: deleteAddress,
} = useDeleteUserAddressMutation();

  /* =====================================================
     SELECTED ITEMS
  ===================================================== */

  const [selectedItems, setSelectedItems] =
    useState([]);


  const selectionInitializedRef =
    useRef(false);


  const removedOutOfStockRef =
    useRef(new Set());


  /* =====================================================
     PAGINATION
  ===================================================== */

  const [currentPage, setCurrentPage] = useState(1);

  const per_page = 5;


  /* =====================================================
     INITIAL SELECT ALL
  ===================================================== */

  useEffect(() => {
    if (
      !selectionInitializedRef.current &&
      cartItems?.length > 0
    ) {
      setSelectedItems(
        cartItems.map((item) => item.id),
      );

      selectionInitializedRef.current = true;
    }
  }, [cartItems]);


  /* =====================================================
     KEEP SELECTED ITEMS SYNCED
  ===================================================== */

  useEffect(() => {
    if (!cartItems) {
      return;
    }

    setSelectedItems((prev) =>
      prev.filter((id) =>
        cartItems.some(
          (item) => item.id === id,
        ),
      ),
    );
  }, [cartItems]);


  /* =====================================================
     REMOVE OUT OF STOCK
  ===================================================== */

  useEffect(() => {
    if (
      !cartItems ||
      cartItems.length === 0
    ) {
      return;
    }

    cartItems.forEach((item) => {
      if (
        item?.product?.qty === 0 &&
        !removedOutOfStockRef.current.has(
          item.id,
        )
      ) {
        removedOutOfStockRef.current.add(
          item.id,
        );

        deleteMutation.mutate(item.id);
      }
    });
  }, [cartItems, deleteMutation]);


  /* =====================================================
     AUTO SELECT BEST SHIPPING
  ===================================================== */




  /* =====================================================
     SHIPPING SELECT
  ===================================================== */

  const handleShippingSelect = (rule) => {
    setSelectedShipping(rule);

    localStorage.setItem(
      "shipping-rules",
      JSON.stringify(rule),
    );
  };


  /* =====================================================
     INCREASE QUANTITY
  ===================================================== */

  const updateQuantityIncrease = (
    productId,
  ) => {
    increaseQty.mutate({
      product_id: productId,
      quantity: 1,
    });
  };


  /* =====================================================
     DECREASE QUANTITY
  ===================================================== */

  const updateQuantityDecrease = (
    productId,
    currentQty,
  ) => {
    if (currentQty <= 1) {
      return;
    }

    decreaseQty.mutate({
      product_id: productId,
      quantity: 1,
    });
  };


  /* =====================================================
     REMOVE SINGLE ITEM
  ===================================================== */

  const removeItem = (id) => {
    deleteMutation.mutate(id);

    setSelectedItems((prev) =>
      prev.filter(
        (itemId) => itemId !== id,
      ),
    );
  };


  /* =====================================================
     SELECT SINGLE ITEM
  ===================================================== */

  const handleSelectItem = (itemId) => {
    setSelectLoading(true);

    setSelectedItems((prev) =>
      prev.includes(itemId)
        ? prev.filter(
            (id) => id !== itemId,
          )
        : [...prev, itemId],
    );

    setTimeout(() => {
      setSelectLoading(false);
    }, 250);
  };


  /* =====================================================
     SELECT ALL
  ===================================================== */

  const isAllSelected =
    cartItems?.length > 0 &&
    cartItems.every((item) =>
      selectedItems.includes(item.id),
    );


  const handleSelectAll = () => {
    setSelectLoading(true);

    setSelectedItems(
      cartItems.map(
        (item) => item.id,
      ),
    );

    setTimeout(() => {
      setSelectLoading(false);
    }, 250);
  };


  /* =====================================================
     DELETE SELECTED
  ===================================================== */

  const handleDeleteSelected =
    async () => {
      if (selectedItems.length === 0) {
        toast.error(
          "Please select items",
        );

        return;
      }

      try {
        setDeleteLoading(true);

        const count =
          selectedItems.length;

        for (
          const itemId of selectedItems
        ) {
          await deleteMutation.mutateAsync(
            itemId,
          );
        }

        setSelectedItems([]);

        toast.success(
          `${count} item(s) removed`,
        );
      } catch (error) {
        console.error(
          "Delete selected error:",
          error,
        );

        toast.error(
          "Failed to remove items",
        );
      } finally {
        setDeleteLoading(false);
      }
    };


  /* =====================================================
     SELECTED CART ITEMS
  ===================================================== */

  const selectedCartItems =
    cartItems.filter((item) =>
      selectedItems.includes(item.id),
    );


  /* =====================================================
     SELECTED ITEM COUNT
  ===================================================== */

  const selectedItemCount =
    selectedCartItems.reduce(
      (acc, item) =>
        acc +
        Number(item.quantity || 1),
      0,
    );


  /* =====================================================
     SUBTOTAL
  ===================================================== */

  const selectedSubtotal =
    selectedCartItems.reduce(
      (acc, item) => {
        const productPrice =
          Number(
            item.product?.price || 0,
          );

        const offerPrice =
          item.product?.offer_price !==
            null &&
          item.product?.offer_price !==
            undefined &&
          item.product?.offer_price !==
            ""
            ? Number(
                item.product.offer_price,
              )
            : null;

        const hasOffer =
          offerPrice !== null &&
          offerPrice < productPrice;

        const finalPrice = hasOffer
          ? offerPrice
          : productPrice;

        return (
          acc +
          finalPrice *
            Number(item.quantity || 1)
        );
      },
      0,
    );

/* =====================================================
   AUTO SELECT SHIPPING BASED ON SELECTED ITEMS
===================================================== */

useEffect(() => {
  if (!shippingRulesData?.length) {
    return;
  }

  const subtotal = Number(selectedSubtotal || 0);

  /* -----------------------------------------------
     FIND AVAILABLE SHIPPING RULES
  ----------------------------------------------- */

  const applicableRules = shippingRulesData.filter((rule) => {
    if (rule.type === "flat_cost") {
      return true;
    }

    if (rule.type === "min_cost") {
      return subtotal >= Number(rule.min_cost || 0);
    }

    return false;
  });

  if (applicableRules.length === 0) {
    setSelectedShipping(null);
    localStorage.removeItem("shipping-rules");
    return;
  }

  /* -----------------------------------------------
     CALCULATE SHIPPING COST
  ----------------------------------------------- */

  const rulesWithCost = applicableRules.map((rule) => {
    let cost = 0;

    if (rule.type === "flat_cost") {
      cost = Number(rule.cost || 0);
    }

    if (rule.type === "min_cost") {
      cost =
        subtotal >= Number(rule.min_cost || 0)
          ? 0
          : Number(rule.cost || 0);
    }

    return {
      rule,
      cost,
    };
  });

  /* -----------------------------------------------
     SELECT CHEAPEST AVAILABLE SHIPPING
  ----------------------------------------------- */

  const bestShipping = rulesWithCost.reduce(
    (best, current) => {
      if (!best || current.cost < best.cost) {
        return current;
      }

      return best;
    },
    null
  );

  if (!bestShipping) {
    return;
  }

  const selectedRule = bestShipping.rule;

  /* -----------------------------------------------
     UPDATE SHIPPING
  ----------------------------------------------- */

  if (selectedShipping?.id !== selectedRule.id) {
    setSelectedShipping(selectedRule);

    localStorage.setItem(
      "shipping-rules",
      JSON.stringify(selectedRule)
    );
  }
}, [
  shippingRulesData,
  selectedSubtotal,
  selectedShipping?.id,
]);
  /* =====================================================
     SHIPPING COST
  ===================================================== */

  const selectedShippingCost =
    Number(
      selectedShipping?.cost || 0,
    );


  /* =====================================================
     TOTAL
  ===================================================== */

  const selectedTotal =
    selectedSubtotal +
    selectedShippingCost;


  /* =====================================================
     SELECT ADDRESS
  ===================================================== */

  const handleSelectAddress = (
    address,
  ) => {
    const normalized =
      normalizeAddress(address);

    setSelectedAddress(normalized);

    localStorage.setItem(
      "checkoutAddress",
      JSON.stringify(normalized),
    );
  };


  /* =====================================================
     FAVORITE ADDRESS
  ===================================================== */

  
/* =====================================================
   EDIT SAVED ADDRESS
===================================================== */

const handleEditAddress = (item) => {
  setEditAddress(item);

  setLocationData({
    latitude: item?.latitude || "",
    longitude: item?.longitude || "",

    address: item?.address || "",

    city:
      item?.city ||
      getNameValue(item?.city_id) ||
      "",

    zip: item?.zip || "",

    state:
      item?.state_name ||
      getNameValue(item?.state_id) ||
      "Telangana",

    country:
      item?.country_name ||
      getNameValue(item?.country_id) ||
      "India",
  });

  setShowLocationPicker(false);
  setShowForm(true);
};


/* =====================================================
   REMOVE SAVED ADDRESS
===================================================== */

const handleDeleteAddress = (id) => {
  Swal.fire({
    title: "Are you sure?",

    text: "You can't get it later!",

    icon: "warning",

    showCancelButton: true,

    confirmButtonText: "Yes, delete it!",

    cancelButtonText: "Cancel",

    confirmButtonColor: "#ff6b00",

    cancelButtonColor: "#777",
  }).then((result) => {
    if (!result.isConfirmed) {
      return;
    }

    deleteAddress(id, {
      onSuccess: () => {
        /* If deleted address was selected */
        if (
          String(selectedAddress?.id) ===
          String(id)
        ) {
          setSelectedAddress(null);

          localStorage.removeItem(
            "checkoutAddress"
          );
        }

        toast.success(
          "Address has been removed."
        );
      },

      onError: () => {
        toast.error(
          "Unable to remove address."
        );
      },
    });
  });
};

  /* =====================================================
     ADDRESS SAVED FROM ADD ADDRESS FORM
  ===================================================== */

  const handleAddressSave = (
    response,
  ) => {
    console.log(
      "Address API response:",
      response,
    );

    const newAddress =
      getAddressFromResponse(
        response,
      );

    if (!newAddress) {
      toast.error(
        "Address saved but could not read address data",
      );

      setShowForm(false);

      return;
    }

    console.log(
      "Normalized address:",
      newAddress,
    );

    setSelectedAddress(
      newAddress,
    );

    localStorage.setItem(
      "checkoutAddress",
      JSON.stringify(newAddress),
    );

    setShowForm(false);

    setLocationData(null);

    toast.success(
      "Address selected",
    );
  };


  /* =====================================================
     ADD NEW ADDRESS
     
     IMPORTANT:
     FIRST OPEN LOCATION PICKER
  ===================================================== */

 const handleAddAddress = () => {
  console.log(
    "Opening location picker",
  );

  setEditAddress(null);

  setLocationData(null);

  setShowForm(false);

  setShowLocationPicker(true);
};

  /* =====================================================
     LOCATION CONFIRMED
     
     AFTER USER SELECTS LOCATION,
     OPEN ADDRESS FORM
  ===================================================== */

  const handleLocationConfirm = (
    selectedLocation,
  ) => {
    console.log(
      "LOCATION CONFIRMED:",
      selectedLocation,
    );

    setLocationData(
      selectedLocation,
    );

    setShowLocationPicker(false);

    setShowForm(true);
  };


  /* =====================================================
     EDIT LOCATION FROM ADDRESS FORM
  ===================================================== */

  const handleEditLocation = () => {
    console.log(
      "Editing location",
    );

    setShowForm(false);

    setShowLocationPicker(true);
  };


  /* =====================================================
     CLOSE LOCATION PICKER
  ===================================================== */

 const handleCloseLocation = () => {
  setShowLocationPicker(false);

  setLocationData(null);
};


  /* =====================================================
     CLOSE ADDRESS FORM
  ===================================================== */

const handleCloseForm = () => {
  setShowForm(false);

  setEditAddress(null);

  setLocationData(null);
};


  /* =====================================================
     CHECKOUT
  ===================================================== */

  const handleCheckOutPayment = () => {

    /* -----------------------------------------------
       PRODUCTS
    ----------------------------------------------- */

    if (selectedItems.length === 0) {
      toast.error(
        "Please select products",
      );

      return;
    }


    /* -----------------------------------------------
       ADDRESS
    ----------------------------------------------- */

    if (!selectedAddress) {
      toast.error(
        "Please select delivery address",
      );

      return;
    }


    /* -----------------------------------------------
       SHIPPING
    ----------------------------------------------- */

    if (!selectedShipping) {
      toast.error(
        "Please select shipping method",
      );

      return;
    }


    /* -----------------------------------------------
       SAVE CHECKOUT DATA
    ----------------------------------------------- */

    setCheckoutLoading(true);

    localStorage.setItem(
      "selectedCheckoutItems",
      JSON.stringify(
        selectedCartItems,
      ),
    );

    localStorage.setItem(
      "checkoutAddress",
      JSON.stringify(
        selectedAddress,
      ),
    );

    localStorage.setItem(
      "shipping-rules",
      JSON.stringify(
        selectedShipping,
      ),
    );


    /* -----------------------------------------------
       NAVIGATE
    ----------------------------------------------- */

    setTimeout(() => {
      navigate(
        "/checkout/payment",
      );
    }, 400);
  };


  /* =====================================================
     PAGINATION
  ===================================================== */

  const indexOfLast =
    currentPage * per_page;

  const indexOfFirst =
    indexOfLast - per_page;

  const currentCartItems =
    cartItems?.slice(
      indexOfFirst,
      indexOfLast,
    );


  /* =====================================================
     ERROR
  ===================================================== */

  if (error) {
    return (
      <section
        className={styles.cart__wrapper}
      >
        <div
          className={
            styles.emptyCartPopup
          }
        >
          <div
            className={
              styles.emptyCartPopupIcon
            }
          >
            <FaShoppingCart />
          </div>

          <h2>
            Your Cart is Empty
          </h2>

          <p>
            Add items to your cart
            to continue shopping.
          </p>

          <button
            type="button"
            className={
              styles.startShoppingBtn
            }
            onClick={() =>
              navigate("/shop")
            }
          >
            Start Shopping
          </button>
        </div>
      </section>
    );
  }


  /* =====================================================
     EMPTY CART
  ===================================================== */

  if (
    !isPending &&
    cartItems?.length === 0
  ) {
    return (
      <section
        className={styles.cart__wrapper}
      >
        <div
          className={
            styles.emptyCartPage
          }
        >
          <div
            className={
              styles.emptyCartIcon
            }
          >
            <FaShoppingCart />
          </div>

          <h2>
            Your Cart is Empty
          </h2>

          <p>
            Looks like you haven't
            added anything to your
            cart yet.
          </p>

          <button
            type="button"
            className={
              styles.continueShoppingBtn
            }
            onClick={() =>
              navigate("/shop")
            }
          >
            Continue Shopping
          </button>
        </div>
      </section>
    );
  }


  /* =====================================================
     UI
  ===================================================== */

  return (
    <section
      className={styles.cart__wrapper}
    >

      {/* =================================================
          TOP BANNER
      ================================================= */}

      <div
        className={styles.cartBanner}
      >
        <div
          className={
            styles.bannerContent
          }
        >
          <span
            className={
              styles.bannerSmallText
            }
          >
            SHOPPING CART
          </span>

          <h1>
            Your Cart
          </h1>

          <p>
            Review your products,
            select your delivery
            address and continue to
            payment.
          </p>
        </div>

        <CheckoutStepper
          currentStep={1}
        />
      </div>


      {/* =================================================
          MAIN LAYOUT
      ================================================= */}

      <div
        className={
          styles.checkoutLayout
        }
      >

        {/* =================================================
            LEFT CONTENT
        ================================================= */}

        <div
          className={
            styles.leftContent
          }
        >

          {/* =================================================
              CART
          ================================================= */}

          <div
            className={
              styles.sectionCard
            }
          >
            <div
              className={
                styles.sectionHeader
              }
            >
              <div>
                <h2>
                  Your Cart
                </h2>
              </div>

              <div
                className={
                  styles.cartActions
                }
              >
                <button
                  type="button"
                  className={
                    styles.selectAllBtn
                  }
                  onClick={
                    handleSelectAll
                  }
                  disabled={
                    selectLoading ||
                    isAllSelected ||
                    cartItems.length ===
                      0
                  }
                >
                  {selectLoading
                    ? "Select All"
                    : "Select All"}
                </button>

                <button
                  type="button"
                  className={
                    styles.deleteSelectedBtn
                  }
                  onClick={
                    handleDeleteSelected
                  }
                  disabled={
                    deleteLoading ||
                    selectedItems.length ===
                      0
                  }
                >
                  {deleteLoading
                    ? "Deleting..."
                    : `Delete Selected (${selectedItems.length})`}
                </button>
              </div>
            </div>


            <CartMainContent
              cartItems={
                currentCartItems
              }
              isLoading={isPending}
              removeItem={
                removeItem
              }
              updateQuantityInc={
                updateQuantityIncrease
              }
              updateQuantityDec={
                updateQuantityDecrease
              }
              styles={styles}
              selectedItems={
                selectedItems
              }
              handleSelectItem={
                handleSelectItem
              }
            />
          </div>


          {/* =================================================
              PAGINATION
          ================================================= */}

          {cartItems?.length >
            per_page && (
            <div
              className={
                styles.paginationWrapper
              }
            >
              <Pagination
                currentPage={
                  currentPage
                }
                total={
                  cartItems.length
                }
                per_page={
                  per_page
                }
                handlePageChange={
                  setCurrentPage
                }
              />
            </div>
          )}


          {/* =================================================
              DELIVERY ADDRESS
          ================================================= */}

          {selectedCartItems.length >
            0 && (
            <div
              className={
                styles.sectionCard
              }
            >

              <div
                className={
                  styles.sectionHeader
                }
              >
                <div>
                  <h2>
                    Delivery Address
                  </h2>

                  <p>
                    Select your
                    preferred delivery
                    address
                  </p>
                </div>
              </div>


              {/* =================================================
                  ADD ADDRESS BUTTON
              ================================================= */}

              <button
                type="button"
                className={
                  styles.addAddressButton
                }
                onClick={
                  handleAddAddress
                }
              >
                <FaPlus size={13} />

                Add New Address
              </button>


              {/* =================================================
                  LOCATION PICKER
                  
                  FIRST STEP
              ================================================= */}

              {showLocationPicker && (
                <LocationPicker
                  onClose={
                    handleCloseLocation
                  }
                  onConfirm={
                    handleLocationConfirm
                  }
                  initialLocation={
                    locationData
                  }
                />
              )}


              {/* =================================================
                  ADDRESS DETAILS FORM
                  
                  SECOND STEP
              ================================================= */}

  {showForm && (
  <div className={styles.formOverlay}>
    <AddMyAddress
      mode={
        editAddress
          ? "edit"
          : "add"
      }

      initialData={
        editAddress
      }

      addressId={
        editAddress?.id
      }

      onClose={
        handleCloseForm
      }

      onSave={
        !editAddress
          ? handleAddressSave
          : undefined
      }

      locationData={
        locationData
      }

      onEditLocation={
        !editAddress
          ? handleEditLocation
          : undefined
      }
    />
  </div>
)}


              {/* =================================================
                  ADDRESS LIST
              ================================================= */}

              <div
                className={
                  styles.addressListWrapper
                }
              >
                <ErrorQueryBoundary
                  fallback={
                    <Loader
                      title={
                        "Loading user addresses.."
                      }
                    />
                  }
                  errorFallback={
                    <ErrorFallback />
                  }
                >
                  {isPendingAddress ? (
                    <Loader
                      title={
                        "Loading user addresses.."
                      }
                    />
                  ) : (
                    <div
                      className={
                        styles.addressSingleCard
                      }
                    >
                      {addressList.length >
                      0 ? (
                        <div
                          className={
                            styles.addressCardsRow
                          }
                        >
                          {addressList.map(
                            (item) => {

                              const isActive =
                                String(
                                  selectedAddress?.id,
                                ) ===
                                String(
                                  item?.id,
                                );

                            

                              const displayName =
                                getNameValue(
                                  item?.name,
                                );

                              const phone =
                                getNameValue(
                                  item?.phone,
                                );

                              const address =
                                getNameValue(
                                  item?.address,
                                );

                              const city =
                                getNameValue(
                                  item?.city,
                                );

                              const state =
                                getNameValue(
                                  item?.state_id,
                                );

                              const country =
                                getNameValue(
                                  item?.country_id,
                                );

                              const zip =
                                getNameValue(
                                  item?.zip,
                                );


                              return (
                                <div
                                  key={
                                    item.id
                                  }
                                  className={`${styles.addressItem} ${
                                    isActive
                                      ? styles.addressItemActive
                                      : ""
                                  }`}
                                  onClick={() =>
                                    handleSelectAddress(
                                      item,
                                    )
                                  }
                                >
                                  <div
                                    className={
                                      styles.addressItemContent
                                    }
                                  >

                                    {/* NAME + FAVORITE */}

                                {/* NAME */}

<div className={styles.addressTopRow}>
  <strong className={styles.addressName}>
    {displayName}
  </strong>
</div>


                                    {/* PHONE */}

                                    {phone && (
                                      <span
                                        className={
                                          styles.addressPhone
                                        }
                                      >
                                        Mob No:{" "}
                                        {
                                          phone
                                        }
                                      </span>
                                    )}


                                    {/* ADDRESS */}

                                    <span
                                      className={
                                        styles.addressLine
                                      }
                                    >
                                      {
                                        address
                                      }

                                      {city
                                        ? `, ${city}`
                                        : ""}

                                      {state
                                        ? `, ${state}`
                                        : ""}

                                      {country
                                        ? `, ${country}`
                                        : ""}
                                    </span>


                                    {/* PIN CODE */}

                                    <span
                                      className={
                                        styles.addressZip
                                      }
                                    >
                                      Pin Code:{" "}
                                      {zip}
                                    </span>


                                    {/* SELECTED BADGE */}

                                  {/* SELECTED BADGE */}


{/* EDIT / REMOVE BELOW SELECTED */}

<div className={styles.addressActionsBottom}>
  <button
    type="button"
    className={styles.editAddressBtn}
    onClick={(e) => {
      e.stopPropagation();
      handleEditAddress(item);
    }}
  >
    Edit
  </button>

  <span className={styles.addressActionSeparator}>
    |
  </span>

  <button
    type="button"
    className={styles.deleteAddressBtn}
    onClick={(e) => {
      e.stopPropagation();
      handleDeleteAddress(item.id);
    }}
  >
    Remove
  </button>
</div>
                                  </div>
                                </div>
                              );
                            },
                          )}
                        </div>
                      ) : (
                        <div
                          className={
                            styles.noAddress
                          }
                        >
                          <p>
                            No saved
                            addresses
                            found.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </ErrorQueryBoundary>
              </div>
            </div>
          )}


          {/* =================================================
              SHIPPING METHOD
          ================================================= */}

          {selectedCartItems.length >
            0 && (
            <div
              className={
                styles.sectionCard
              }
            >
              <div
                className={
                  styles.shippingWrapper
                }
              >
                {isShippingPending ? (
                  <Loader
                    title={
                      "Loading shipping methods..."
                    }
                  />
                ) : (
                  <CheckoutShipping
                    shippingRules={
                      shippingRulesData
                    }
                    selectedShipping={
                      selectedShipping
                    }
                    setSelectedShipping={
                      handleShippingSelect
                    }
                    subtotal={
                      selectedSubtotal
                    }
                  />
                )}
              </div>
            </div>
          )}


          {/* =================================================
              TRUST CARDS
          ================================================= */}

          {selectedCartItems.length >
            0 && (
            <div
              className={
                styles.trustGrid
              }
            >
              <div
                className={
                  styles.trustCard
                }
              >
                <span>
                  🔒
                </span>

                <div>
                  <strong>
                    Safe & Secure
                    Payments
                  </strong>

                  <p>
                    100% secure
                    checkout
                  </p>
                </div>
              </div>


              <div
                className={
                  styles.trustCard
                }
              >
                <span>
                  ↩️
                </span>

                <div>
                  <strong>
                    Easy Returns
                  </strong>

                  <p>
                    Hassle-free
                    returns
                  </p>
                </div>
              </div>


              <div
                className={
                  styles.trustCard
                }
              >
                <span>
                  🎧
                </span>

                <div>
                  <strong>
                    24/7 Support
                  </strong>

                  <p>
                    We are here to
                    help
                  </p>
                </div>
              </div>
            </div>
          )}

        </div>


        {/* =================================================
            RIGHT SUMMARY
        ================================================= */}

        {selectedCartItems.length >
          0 && (
          <aside
            className={
              styles.rightSummary
            }
          >
            <CartOrderSummary
              styles={styles}
              subtotal={
                selectedSubtotal
              }
              shippingCost={
                selectedShippingCost
              }
              tax={0}
              totalAmount={
                selectedTotal
              }
              onButtonClick={
                handleCheckOutPayment
              }
              loading={
                checkoutLoading
              }
              selectedCount={
                selectedItemCount
              }
            />
          </aside>
        )}

      </div>

    </section>
  );
};

export default Cart;